// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { setNodes as setSelectedNodes } from '../../../store/slices/selectionSlice'
import { getNode } from '../../../services/node'
import { mergeSessionEntities } from '../../../store/slices/sessionSlice'
import { useTextareaControls } from './useTextareaControls'
import { useSessionRefresh } from './useSessionRefresh'
import { ChatService } from '../services/chatService'
import {
    setInput,
    setInteractive
} from '../../../store/slices/chatSlice'
import { useFileHandler } from './useFileHandler'
import { useChatActions } from './useChatActions'
import { useRecommendations } from './useRecommendations'
import { useSessionRecovery } from './useSessionRecovery'
import { selectActiveSession, selectActiveSessionId } from '../../../store/selectors/sessionSelectors'
import {
    selectActiveChatMessages,
    selectActiveChatInput,
    selectActiveInteractive,
    selectActiveContextUsage,
} from '../../../store/selectors/chatSelectors'
import { selectActiveSelectedNodeIds } from '../../../store/selectors/selectionSelectors'

/**
 * Main controller hook for Backstage chat and interaction.
 * Manages message history, text input, file uploads, and orchestrates the agent loop.
 */
export function useBackstageController() {
    // Global State
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const selectedNodeIds = useAppSelector(selectActiveSelectedNodeIds)
    const refreshSession = useSessionRefresh(activeSessionId)

    // Chat State
    const messages = useAppSelector(selectActiveChatMessages)
    const input = useAppSelector(selectActiveChatInput)
    const interactive = useAppSelector(selectActiveInteractive)
    const contextUsage = useAppSelector(selectActiveContextUsage)

    // Refs
    const bottomRef = useRef<HTMLDivElement | null>(null)
    const messagesContainerRef = useRef<HTMLDivElement | null>(null)
    const scrollTopBySessionIdRef = useRef<Record<string, number>>({})
    const lastAutoScrollSessionIdRef = useRef<string | null>(null)

    // Actions Hook
    const { handleSend, handleSendWithText, canSend } = useChatActions()

    // File Handler Hook
    const { fileInputRef, handlePickFile, handleFileChange } = useFileHandler(session, refreshSession)

    // Textarea Controls
    const handleSetInput = useCallback((val: string) => {
        if (!activeSessionId) return
        dispatch(setInput({ sessionId: activeSessionId, input: val }))
    }, [dispatch, activeSessionId])

    const {
        textareaRef,
        handleKeyDown,
        handleCompositionStart,
        handleCompositionEnd,
    } = useTextareaControls(input, handleSetInput, handleSend)

    const {
        recommendations,
        isLoading: isRecommendationsLoading,
        isManualRefreshing: isRecommendationsManualRefreshing,
        hasStaleContext: hasStaleRecommendationsContext,
        refreshRecommendations,
    } = useRecommendations()

    // Recover from background processing after page refresh / reconnect
    useSessionRecovery(activeSessionId, refreshSession)

    const lastSyncedSessionId = useRef<string | null>(null)
    const lastSyncedMessages = useRef<unknown>(undefined)

    // --- Effects ---

    // Hydrate chat from session history on load/change
    useEffect(() => {
        // Only sync if we have a session and it's a different one, or if the session
        // is null but we previously had one (clearing state), or if the messages reference
        // has changed (e.g. hydration after placeholder)
        const isNewSession = session?.id && session.id !== lastSyncedSessionId.current
        const messagesChanged = session?.messages !== lastSyncedMessages.current
        const isClearedSession = !session && lastSyncedSessionId.current

        if (!activeSessionId) return
        if (isNewSession || (session?.id && messagesChanged) || isClearedSession) {
            ChatService.syncMessages(dispatch, activeSessionId, session)
            lastSyncedSessionId.current = session?.id || null
            lastSyncedMessages.current = session?.messages
        }
    }, [dispatch, session, activeSessionId])

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (!activeSessionId) return
        if (lastAutoScrollSessionIdRef.current !== activeSessionId) {
            lastAutoScrollSessionIdRef.current = activeSessionId
            return
        }
        const container = messagesContainerRef.current
        if (!container) return
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
        if (distanceFromBottom > 48) return
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }, [messages.length, activeSessionId])

    useEffect(() => {
        if (!activeSessionId) return
        const container = messagesContainerRef.current
        if (!container) return
        const saved = scrollTopBySessionIdRef.current[activeSessionId]
        if (typeof saved === 'number') {
            container.scrollTop = saved
        }
    }, [activeSessionId])

    const handleMessagesScroll = useCallback(() => {
        if (!activeSessionId) return
        const container = messagesContainerRef.current
        if (!container) return
        scrollTopBySessionIdRef.current[activeSessionId] = container.scrollTop
    }, [activeSessionId])

    // --- Handlers ---

    const handleInteractiveImageFinalize = useCallback((nid: string) => {
        if (!activeSessionId) return
        getNode(nid)
            .then((node) => {
                dispatch(mergeSessionEntities({ sessionId: activeSessionId, nodes: [node] }))
                dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: [nid] }))
            })
            .catch(() => { })
        if (session?.id) {
            refreshSession()
        }
    }, [dispatch, session?.id, refreshSession, activeSessionId])

    const handleSetInteractive = useCallback((state: any) => {
        if (!activeSessionId) return
        dispatch(setInteractive({ sessionId: activeSessionId, interactive: state }))
    }, [dispatch, activeSessionId])

    return {
        session,
        selectedNodeIds,
        input,
        messages,
        interactive,
        contextUsage,
        bottomRef,
        messagesContainerRef,
        fileInputRef,
        textareaRef,
        canSend,
        setInput: handleSetInput,
        setInteractive: handleSetInteractive,
        handleSend,
        handleSendWithText,
        handleKeyDown,
        handleCompositionStart,
        handleCompositionEnd,
        handlePickFile,
        handleFileChange,
        handleInteractiveImageFinalize,
        handleMessagesScroll,
        recommendations,
        isRecommendationsLoading,
        isRecommendationsManualRefreshing,
        hasStaleRecommendationsContext,
        refreshRecommendations,
    }
}
